// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.
/*
 * Есть два варианта:
 *
 * Вариант A — ручное выставление флага
 * Сгенерированное version.json заливается на FTP с deployReady: false.
 * Админ проверяет, что новые pods успешно запущены.
 * Меняет deployReady на true (через FTP или скрипт CI/CD):
 *
 * jq '.deployReady = true' version.json | lftp -u "$FTP_USER","$FTP_PASS" "$FTP_SERVER" -e "put -O ${FTP_REMOTE_PATH:-/} - version.json; bye"
 *
 * Вариант B — автоматический флаг через CI/CD
 * После успешного деплоя Helm/K8s скрипт делает:
 *
 * curl -X PATCH -d '{"deployReady":true}' https://cdn.example.com/version.json
 * # или перезаливка version.json с deployReady: true
 */
import React, { useCallback, useEffect, useState } from 'react';
import '@/styles/version-checker.css';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';
import { CDN_VERSION_URL, DEVELOPMENT } from '@/config.ts';
import { VersionData } from '@/types/version-data.ts';

/**
 * version from env / build system
 * @example
 * docker build --build-arg TAG_VERSION=${VERSION_TAG} \
 *                         -t ${DOCKER_REGISTRY}/${IMAGE_NAME}:${VERSION_TAG} .
 * docker build --build-arg TAG_VERSION=$(git rev-parse --short HEAD) .
 */
const CURRENT_VERSION = import.meta.env.VITE_TAG_VERSION || '1.0.0';
const REMOTE_VERSION_URL =
  import.meta.env.VITE_REMOTE_VERSION_URL ||
  CDN_VERSION_URL ||
  'https://ccalls.site/cdn/dist/version.json';


/**
 * Polls every 10 seconds version from localStorage compares with current version (build)
 *
 * TODO: need survive API down/up kubectl delete. because when API updated via delete/remove -> public/assets errors vite.
 * @beta
 */
export const VersionChecker: React.FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkRemoteVersion = useCallback(async () => {
    try {
      const response = await fetch(`${REMOTE_VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return;

      const data: VersionData = await response.json();
      const latestVersion = data.version;
      const deployReady = data.deployReady;

      if (!latestVersion || !deployReady) return;

      if (latestVersion && deployReady && latestVersion !== CURRENT_VERSION) {
        setUpdateAvailable(true);
      }
      if (latestVersion && deployReady && latestVersion !== CURRENT_VERSION) {
        if (DEVELOPMENT) return;
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((reg) => reg.update());
          window.location.reload();
        });
        // window.location.href = window.location.pathname + '?v=' + Date.now();
        window.location.replace(window.location.pathname + '?v=' + Date.now());

        window.addEventListener(
          'error',
          (event) => {
            const target = event.target as HTMLElement;

            if (target instanceof HTMLScriptElement || target instanceof HTMLLinkElement) {
              location.reload();
            }
          },
          true
        );
        window.addEventListener('unhandledrejection', (event) => {
          if (String(event.reason).includes('Failed to fetch dynamically imported module')) {
            location.reload();
          }
        });
      }

      const storedVersion = localStorage.getItem('latestVersion');
      if (!storedVersion) {
        localStorage.setItem('latestVersion', CURRENT_VERSION);
      }
    } catch (e) {
      console.warn('Version check failed', e);
    }
  }, []);

  useEffect(() => {
    checkRemoteVersion();
    const interval = setInterval(checkRemoteVersion, 20_000); // TODO: maybe 30_000 sec ?
    return () => clearInterval(interval);
  }, [checkRemoteVersion]);

  useEffect(() => {
    const pending = localStorage.getItem('pendingVersionReload');
    if (pending && pending === CURRENT_VERSION) {
      localStorage.removeItem('pendingVersionReload');
    }
  }, []);

  const handleReload = useCallback(() => {
    localStorage.setItem('latestVersion', CURRENT_VERSION);
    localStorage.setItem('pendingVersionReload', CURRENT_VERSION);

    // TODO: из-за serviceWorker может залипать бесконечная загрузка?
    try {
      if ('caches' in window) {
        caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
      }
    } catch (e) {
      console.error(e);
    }

    // TODO: rewrite to sync ver
    // try {
    //   if ('serviceWorker' in navigator) {
    //     const registrations = await navigator.serviceWorker.getRegistrations();
    //
    //
    //       registrations.map(async (registration) => {
    //         try {
    //           await registration.update();
    //
    //           // если уже скачан новый SW — активируем сразу
    //           if (registration.waiting) {
    //             registration.waiting.postMessage({
    //               type: 'SKIP_WAITING',
    //             });
    //           }
    //         } catch (e) {
    //           console.error(e);
    //         }
    //       })
    //
    //   }
    // } catch (e) {
    //   console.error('Failed to update service worker', e);
    // }

    // Electron
    if (isPardusBrowser()) {
      try {
        if (!window.electronAPI) return;
        if (window.electronAPI?.reloadPage) {
          window.electronAPI.reloadPage();
          // return;
        }
      } catch (e) {
        console.error(e);
      }
    }

    // Web browser
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('__update', Date.now().toString());

      // replace + forced reload ensures no back navigation to stale state
      window.location.replace(url.toString());

      // fallback safety
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      console.error(e);

      // fallback
      window.location.reload();
    }

  }, []);

  useEffect(() => {
    if (!updateAvailable) return;
    if (DEVELOPMENT) return;
    if (localStorage.getItem('pendingVersionReload')) return; // уже перезагружались
    const autoReloadInterval = setInterval(handleReload, 3_000);
    return () => clearInterval(autoReloadInterval);
  }, [updateAvailable]);

  if (!updateAvailable) return null;

  return (
    <div
      className={`version-checker-button ${updateAvailable ? 'show' : ''}`}
      onClick={handleReload}
    >
      Обновить Calls
    </div>
  );
};